"use client";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import axios from "../axios-base";
import { toast } from "sonner";
import { calculateDeliveryPricing } from "lib/quantity-discount";

export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      isLoading: false,

      setIsOpen: (isOpen) => set({ isOpen }),

      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),

      // Validate and clean cart items on load
      validateItems: () => {
        const { items } = get();
        const validItems = items
          .map((item) => {
            // Check if item has valid structure
            if (!item?.book || !item?.qty) return null;

            const bookData = typeof item.book === "object" ? item.book : null;
            if (!bookData?._id || !bookData?.price) return null;

            // Ensure qty is reasonable (max 999)
            const qty = Number(item.qty) || 1;
            if (qty < 1 || qty > 999) return null;

            return {
              book: {
                _id: bookData._id,
                name: bookData.name,
                slug: bookData.slug,
                cover: bookData.cover,
                images: bookData.images,
                price: bookData.price,
                author: bookData.author,
              },
              qty: qty,
            };
          })
          .filter(Boolean);

        // Update if validation changed anything
        if (validItems.length !== items.length) {
          set({ items: validItems });
        }
      },

      // Helpers
      calcTotal: () => {
        const { items } = get();
        return items.reduce((total, item) => {
          // Handle both populated (object) and ID-only (string) book references
          const bookData = typeof item.book === "object" ? item.book : null;
          const price = bookData?.price?.delivery?.price || 0;
          const discount = bookData?.price?.delivery?.discount || 0;
          const qty = item.qty || 1;
          const quantityDiscountRules =
            bookData?.price?.delivery?.quantityDiscountRules || [];
          const pricing = calculateDeliveryPricing({
            price,
            discount,
            quantity: qty,
            quantityDiscountRules,
          });
          return total + pricing.finalTotal;
        }, 0);
      },

      /**
       * Add Item to Cart
       * @param {Object} book - Full book object (must have _id, name, price, cover)
       * @param {Number} qty - Quantity to add (default 1)
       * @param {Object|null} user - Current user object (if logged in)
       */
      addToCart: async (book, qty = 1, user = null) => {
        const { items } = get();

        // Check stock availability
        const stockQty = book?.price?.delivery?.quantity || 0;
        const bookId = book._id || book;
        const existingIdx = items.findIndex((i) => {
          const itemBookId = i.book._id || i.book;
          return String(itemBookId) === String(bookId);
        });
        const currentQty = existingIdx > -1 ? items[existingIdx].qty : 0;
        const newQty = currentQty + qty;

        if (newQty > stockQty) {
          if (typeof window !== "undefined" && toast?.error) {
            toast.error(`Үлдэгдэл хүрэлцэхгүй байна. Үлдэгдэл: ${stockQty}`, {
              duration: 3000,
            });
          }
          return;
        }

        let newItems = [...items];

        if (existingIdx > -1) {
          newItems[existingIdx].qty = newQty;
        } else {
          // Store normalized book object
          newItems.push({
            book: {
              _id: book._id,
              name: book.name,
              slug: book.slug,
              cover: book.cover,
              images: book.images,
              price: book.price,
              author: book.author,
            },
            qty,
          });
        }

        set({ items: newItems });
        set({ isOpen: true }); // Open cart when adding

        // If user is logged in, sync with server
        if (user) {
          set({ isLoading: true });
          try {
            const finalQty = existingIdx > -1 ? newItems[existingIdx].qty : qty;

            const res = await axios.post("gerelt/cart", {
              bookId: book._id,
              qty: finalQty,
            });

            if (res.data.success && res.data.data?.items) {
              // Normalize items from server response
              const normalizedItems = res.data.data.items.map((item) => ({
                book: {
                  _id: item.book._id || item.book,
                  name: item.book.name,
                  slug: item.book.slug,
                  cover: item.book.cover,
                  images: item.book.images,
                  price: item.book.price,
                  author: item.book.author,
                },
                qty: item.qty,
              }));
              set({ items: normalizedItems });
            }
          } catch (err) {
            const errorMsg =
              err?.response?.data?.error || "Сагсанд нэмж чадсангүй";
            if (typeof window !== "undefined" && toast?.error) {
              toast.error(errorMsg, { duration: 3000 });
            }
            // Revert on error
            set({ items: items });
          } finally {
            set({ isLoading: false });
          }
        }
      },

      /**
       * Update Item Quantity
       * @param {String} bookId
       * @param {Number} qty
       * @param {Object|null} user
       */
      updateQty: async (bookId, qty, user = null) => {
        if (qty < 1) return;
        const { items } = get();

        // Find item and check stock
        const bookIdStr = String(bookId);
        const item = items.find((i) => {
          const itemBookId = i.book._id || i.book;
          return String(itemBookId) === bookIdStr;
        });

        if (item) {
          const bookData = typeof item.book === "object" ? item.book : null;
          const stockQty = bookData?.price?.delivery?.quantity || 0;
          if (qty > stockQty) {
            if (typeof window !== "undefined" && toast?.error) {
              toast.error(`Үлдэгдэл хүрэлцэхгүй байна. Үлдэгдэл: ${stockQty}`, {
                duration: 3000,
              });
            }
            return;
          }
        }

        const newItems = items.map((item) => {
          const itemBookId = item.book._id || item.book;
          return String(itemBookId) === bookIdStr ? { ...item, qty } : item;
        });

        set({ items: newItems });

        if (user) {
          try {
            const res = await axios.post("gerelt/cart", { bookId, qty });
            if (res.data.success && res.data.data?.items) {
              // Normalize and update with server response
              const normalizedItems = res.data.data.items.map((item) => ({
                book: {
                  _id: item.book._id || item.book,
                  name: item.book.name,
                  slug: item.book.slug,
                  cover: item.book.cover,
                  images: item.book.images,
                  price: item.book.price,
                  author: item.book.author,
                },
                qty: item.qty,
              }));
              set({ items: normalizedItems });
            }
          } catch (err) {
            const errorMsg =
              err?.response?.data?.error || "Тоо ширхэг өөрчлөхөд алдаа гарлаа";
            if (typeof window !== "undefined" && toast?.error) {
              toast.error(errorMsg, { duration: 3000 });
            }
            // Revert on error
            set({ items: items });
          }
        }
      },

      /**
       * Remove Item
       * @param {String} bookId
       * @param {Object|null} user
       */
      removeItem: async (bookId, user = null) => {
        const { items } = get();
        const bookIdStr = typeof bookId === "string" ? bookId : String(bookId);

        const newItems = items.filter((i) => {
          const itemBookId = i.book._id || i.book;
          return String(itemBookId) !== bookIdStr;
        });
        set({ items: newItems });

        if (user) {
          try {
            const res = await axios.delete(`gerelt/cart/${bookIdStr}`);

            if (res.data.success && res.data.data?.items) {
              // Normalize and update with server response
              const normalizedItems = res.data.data.items.map((item) => ({
                book: {
                  _id: item.book._id || item.book,
                  name: item.book.name,
                  slug: item.book.slug,
                  cover: item.book.cover,
                  images: item.book.images,
                  price: item.book.price,
                  author: item.book.author,
                },
                qty: item.qty,
              }));
              set({ items: normalizedItems });
            }
          } catch (err) {
            // Revert on error
            set({ items });
          }
        }
      },

      /**
       * Fetch Cart from Server
       * Always fetches fresh data from server (no merge with localStorage)
       * @param {Object} user
       */
      fetchCart: async (user) => {
        if (!user) return;
        set({ isLoading: true });
        try {
          const res = await axios.get("gerelt/cart");

          if (res.data.success) {
            // Normalize items: store minimal book data
            const normalizedItems = res.data.data.items.map((item) => ({
              book: {
                _id: item.book._id || item.book,
                name: item.book.name,
                slug: item.book.slug,
                cover: item.book.cover,
                images: item.book.images,
                price: item.book.price,
                author: item.book.author,
              },
              qty: item.qty,
            }));
            set({ items: normalizedItems });
          }
        } catch (err) {
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * Sync Local Cart with Server on Login
       * Only use this when user logs in (merge guest cart with DB cart)
       * @param {Object} user
       */
      syncOnLogin: async (user) => {
        if (!user) return;
        const { items } = get();

        // If local cart is empty, just fetch server cart
        if (items.length === 0) {
          return get().fetchCart(user);
        }
        set({ isLoading: true });
        try {
          // Prepare payload: [{ book: id, qty: n }]
          const payload = items.map((i) => ({
            book: i.book._id || i.book,
            qty: i.qty,
          }));

          const res = await axios.post("gerelt/cart/sync", { items: payload });
          if (res.data.success) {
            // Normalize items: store minimal book data
            const normalizedItems = res.data.data.items.map((item) => ({
              book: {
                _id: item.book._id || item.book,
                name: item.book.name,
                slug: item.book.slug,
                cover: item.book.cover,
                images: item.book.images,
                price: item.book.price,
                author: item.book.author,
              },
              qty: item.qty,
            }));
            set({ items: normalizedItems });

            // Show warnings if any items exceeded stock
            if (res.data.warnings && res.data.warnings.length > 0) {
              res.data.warnings.forEach((warning) => {
                if (typeof window !== "undefined" && toast?.warning) {
                  toast.warning(warning.message, { duration: 5000 });
                }
              });
            }
          }
        } catch (err) {
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * Clear All Cart Items
       * @param {Object|null} user
       */
      clearCart: async (user = null) => {
        set({ items: [] });

        if (user) {
          try {
            await axios.delete("gerelt/cart");
          } catch (err) {
          }
        }
      },
    }),
    {
      name: "cart-storage", // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
      partialize: (state) => ({ items: state.items }), // Only persist items
      version: 1, // Add version for future migrations
      migrate: (persistedState, version) => {
        // Migration logic for localStorage data
        if (version === 0 && persistedState?.items) {
          // Normalize old data format
          const normalizedItems = persistedState.items
            .map((item) => {
              // Skip invalid items
              if (!item?.book || !item?.qty) return null;

              const bookData = typeof item.book === "object" ? item.book : null;
              if (!bookData?._id) return null;

              // Ensure qty is reasonable (max 999)
              const qty = Number(item.qty) || 1;
              if (qty < 1 || qty > 999) return null;

              // Normalize book object structure
              return {
                book: {
                  _id: bookData._id,
                  name: bookData.name,
                  slug: bookData.slug,
                  cover: bookData.cover,
                  images: bookData.images,
                  price: bookData.price,
                  author: bookData.author,
                },
                qty: qty,
              };
            })
            .filter(Boolean);

          return { items: normalizedItems };
        }
        return persistedState;
      },
      onRehydrateStorage: () => (state) => {
        // Validate items after rehydration
        if (state?.validateItems) {
          state.validateItems();
        }
      },
    },
  ),
);
